import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, Calendar, Camera, User, Settings2, ChevronLeft, ChevronRight, Images } from 'lucide-react';
import { apiFetch } from '../lib/api';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import { smoothScrollToElement } from '../lib/smoothScroll';

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm'];
const SHOW_LEGACY_GALLERY_FALLBACK = false;
const LEGACY_GALLERY_FALLBACK_IMAGE = '/media/site_image1.jpg';
const STORMS_PER_PAGE = 6;

const isVideoMedia = (mediaUrl = '') => {
  if (typeof mediaUrl !== 'string') {
    return false;
  }

  const normalizedUrl = mediaUrl.split(/[?#]/, 1)[0].toLowerCase();
  return VIDEO_EXTENSIONS.some((extension) => normalizedUrl.endsWith(extension));
};

const formatStormDate = (dateValue = '') => {
  if (typeof dateValue !== 'string') {
    return '';
  }

  const trimmedDate = dateValue.trim();
  if (!trimmedDate || trimmedDate.includes(',')) {
    return trimmedDate;
  }

  const match = trimmedDate.match(/^([A-Za-z]+)\s+(\d{1,2}(?:st|nd|rd|th)?)\s+(\d{4})$/);
  if (!match) {
    return trimmedDate;
  }

  return `${match[1]} ${match[2]}, ${match[3]}`;
};

const parseStormDate = (dateValue = '') => {
  if (typeof dateValue !== 'string') {
    return null;
  }

  const normalizedDate = formatStormDate(dateValue)
    .replace(/(\d{1,2})(st|nd|rd|th)/i, '$1')
    .trim();
  const parsedDate = new Date(normalizedDate);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

const isRecentStorm = (dateValue = '') => {
  const stormDate = parseStormDate(dateValue);
  if (!stormDate) {
    return false;
  }

  const now = new Date();
  const currentDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const capturedDay = new Date(stormDate.getFullYear(), stormDate.getMonth(), stormDate.getDate());
  const diffInDays = Math.round((currentDay.getTime() - capturedDay.getTime()) / 86400000);

  return diffInDays >= 0 && diffInDays <= 7;
};

const getPrimaryStormPreview = (storm) => {
  if (typeof storm?.src === 'string' && storm.src && !isVideoMedia(storm.src)) {
    return storm.src;
  }

  const mediaItems = Array.isArray(storm?.images) ? storm.images : [];
  return mediaItems.find((mediaUrl) => !isVideoMedia(mediaUrl))
    || mediaItems[0]
    || (SHOW_LEGACY_GALLERY_FALLBACK ? LEGACY_GALLERY_FALLBACK_IMAGE : null);
};

const getStormRows = (stormItems) => {
  if (stormItems.length === 7) {
    return [
      stormItems.slice(0, 2),
      stormItems.slice(2, 5),
      stormItems.slice(5, 7),
    ];
  }

  const rows = [];
  for (let index = 0; index < stormItems.length; index += 3) {
    rows.push(stormItems.slice(index, index + 3));
  }
  return rows;
};

const GallerySection = () => {
  const [storms, setStorms] = useState([]);
  const [isLoadingStorms, setIsLoadingStorms] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedStorm, setSelectedStorm] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [stormPage, setStormPage] = useState(0);
  const stormCountLabel = `${storms.length} documented ${storms.length === 1 ? 'storm' : 'storms'} from the field. Open any storm to browse every captured frame.`;

  useBodyScrollLock(Boolean(selectedStorm));

  useEffect(() => {
    let isMounted = true;

    const loadStorms = async () => {
      try {
        const response = await apiFetch('/api/storms?limit=0&auto_import=true');
        if (!response.ok) {
          throw new Error('storm gallery request failed');
        }

        const payload = await response.json();
        if (!Array.isArray(payload)) {
          throw new Error('invalid storm gallery payload');
        }

        if (isMounted) {
          setStorms(payload);
          setStormPage(0);
          setLoadError(false);
        }
      } catch {
        if (isMounted) {
          setStorms([]);
          setLoadError(true);
          setSelectedStorm(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingStorms(false);
        }
      }
    };

    loadStorms();
    return () => {
      isMounted = false;
    };
  }, []);

  const pageCount = Math.max(1, Math.ceil(storms.length / STORMS_PER_PAGE));
  const visibleStorms = useMemo(() => {
    const startIndex = stormPage * STORMS_PER_PAGE;
    return storms.slice(startIndex, startIndex + STORMS_PER_PAGE);
  }, [stormPage, storms]);

  useEffect(() => {
    if (stormPage > 0 && stormPage >= pageCount) {
      setStormPage(pageCount - 1);
    }
  }, [pageCount, stormPage]);

  useEffect(() => {
    if (!selectedStorm) {
      return;
    }

    const refreshedSelectedStorm = storms.find(
      (storm) => String(storm.id) === String(selectedStorm.id)
    );
    if (!refreshedSelectedStorm) {
      setSelectedStorm(null);
      setActiveImageIndex(0);
      return;
    }

    setSelectedStorm(refreshedSelectedStorm);
    setActiveImageIndex((currentIndex) => {
      if (!Array.isArray(refreshedSelectedStorm.images) || refreshedSelectedStorm.images.length === 0) {
        return 0;
      }
      return Math.min(currentIndex, refreshedSelectedStorm.images.length - 1);
    });
  }, [storms, selectedStorm]);

  useEffect(() => {
    if (!selectedStorm) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedStorm(null);
        return;
      }

      if (selectedStorm.images.length <= 1) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        setActiveImageIndex((currentIndex) => (
          currentIndex === 0 ? selectedStorm.images.length - 1 : currentIndex - 1
        ));
      }

      if (event.key === 'ArrowRight') {
        setActiveImageIndex((currentIndex) => (
          currentIndex === selectedStorm.images.length - 1 ? 0 : currentIndex + 1
        ));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedStorm]);

  const openStorm = (storm) => {
    setSelectedStorm(storm);
    setActiveImageIndex(0);
  };

  const closeStorm = () => {
    setSelectedStorm(null);
    setActiveImageIndex(0);
  };

  const changeStormPage = (nextPage) => {
    setStormPage(nextPage);
    smoothScrollToElement('#gallery');
  };

  const showPreviousImage = () => {
    if (!selectedStorm || selectedStorm.images.length <= 1) {
      return;
    }

    setActiveImageIndex((currentIndex) => (
      currentIndex === 0 ? selectedStorm.images.length - 1 : currentIndex - 1
    ));
  };

  const showNextImage = () => {
    if (!selectedStorm || selectedStorm.images.length <= 1) {
      return;
    }

    setActiveImageIndex((currentIndex) => (
      currentIndex === selectedStorm.images.length - 1 ? 0 : currentIndex + 1
    ));
  };

  const selectedStormMedia = Array.isArray(selectedStorm?.images) ? selectedStorm.images : [];
  const activeMedia = selectedStormMedia[activeImageIndex] || null;
  const activeMediaIsVideo = isVideoMedia(activeMedia);
  const stormRows = getStormRows(visibleStorms);

  return (
    <section id="gallery" className="py-24 bg-gradient-to-b from-black via-gray-900 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ amount: 0.2 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 bg-red-500/20 text-red-400 rounded-full text-sm font-medium mb-4">
            Our Work
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Storm Gallery
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {stormCountLabel}
          </p>
        </motion.div>

        {/* Gallery Grid */}
        {loadError ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 rounded-2xl bg-gray-900/50 border border-gray-800"
          >
            <p className="text-gray-300 text-lg">
              Unable to retrieve storm gallery items, please try again later.
            </p>
          </motion.div>
        ) : isLoadingStorms ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <p className="text-gray-400 text-lg">Loading storm gallery...</p>
          </motion.div>
        ) : storms.length > 0 ? (
          <div className="space-y-6">
            {stormRows.map((row, rowIndex) => (
              <div
                key={`storm-row-${rowIndex}`}
                className="flex flex-wrap justify-center gap-6"
              >
                {row.map((storm, itemIndex) => {
                  const primaryMedia = getPrimaryStormPreview(storm);
                  const recentStorm = isRecentStorm(storm.date);
                  const absoluteIndex = stormRows
                    .slice(0, rowIndex)
                    .reduce((count, currentRow) => count + currentRow.length, 0) + itemIndex;

                  return (
                    <motion.div
                      key={storm.id}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ amount: 0.2 }}
                      transition={{ delay: absoluteIndex * 0.1 }}
                      onClick={() => openStorm(storm)}
                      className="group relative aspect-[4/3] w-full shrink-0 cursor-pointer overflow-hidden rounded-xl sm:basis-[calc((100%-1.5rem)/2)] md:basis-[calc((100%-3rem)/3)]"
                    >
                      {!primaryMedia ? (
                        <div className="w-full h-full bg-gray-800" />
                      ) : isVideoMedia(primaryMedia) ? (
                        <video
                          src={primaryMedia}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          muted
                          playsInline
                          preload="metadata"
                          loop
                          autoPlay
                        />
                      ) : (
                        <img
                          src={primaryMedia}
                          alt={storm.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-300" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                        <h3 className="text-white font-bold text-lg mb-1">{storm.title}</h3>
                        <div className="flex items-center gap-2 text-gray-300 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <MapPin size={14} />
                          <span>{storm.location}</span>
                        </div>
                      </div>
                      <div className="absolute top-4 left-4 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full flex items-center gap-2 text-white text-xs font-semibold">
                        <Images size={14} />
                        <span>{storm.imageCount} {storm.imageCount === 1 ? 'Media Item' : 'Media Items'}</span>
                      </div>
                      {recentStorm && (
                        <div className="absolute top-4 right-4 px-3 py-1.5 bg-amber-400/90 text-black rounded-full text-xs font-black uppercase tracking-[0.2em] shadow-lg shadow-amber-500/20">
                          Recent
                        </div>
                      )}
                      <div className={`absolute w-10 h-10 bg-red-500/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transform scale-0 group-hover:scale-100 transition-all duration-300 ${
                        recentStorm ? 'top-16 right-4' : 'top-4 right-4'
                      }`}>
                        <span className="text-white text-sm font-bold">+</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ))}
            {storms.length > STORMS_PER_PAGE && (
              <div className="flex flex-wrap items-center justify-center gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => changeStormPage(Math.max(0, stormPage - 1))}
                  disabled={stormPage === 0}
                  className="rounded-full border border-white/15 px-5 py-2 text-sm font-bold text-white transition hover:border-red-400 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="text-sm font-semibold text-gray-400">
                  Page {stormPage + 1} of {pageCount}
                </span>
                <button
                  type="button"
                  onClick={() => changeStormPage(Math.min(pageCount - 1, stormPage + 1))}
                  disabled={stormPage >= pageCount - 1}
                  className="rounded-full border border-white/15 px-5 py-2 text-sm font-bold text-white transition hover:border-red-400 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 rounded-2xl bg-gray-900/50 border border-gray-800"
          >
            <p className="text-gray-300 text-lg">
              No storm gallery items are currently listed.
            </p>
          </motion.div>
        )}
      </div>

      {/* Photo Detail Modal */}
      <AnimatePresence>
        {selectedStorm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/90 p-3 backdrop-blur-md sm:p-4 lg:items-center"
            onClick={closeStorm}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative my-auto w-full max-w-5xl rounded-2xl bg-gray-900 shadow-2xl overflow-y-auto overscroll-contain max-h-[calc(100vh-1.5rem)] sm:max-h-[90vh] lg:overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeStorm}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 hover:bg-red-500 rounded-full flex items-center justify-center text-white transition-colors duration-200"
              >
                <X size={20} />
              </button>

              <div className="flex flex-col lg:max-h-[90vh] lg:flex-row">
                {/* Image */}
                <div className="lg:w-3/5 bg-black">
                  <div className="relative h-[32vh] min-h-[200px] sm:h-[42vh] sm:min-h-[260px] lg:h-full lg:min-h-[320px]">
                    <AnimatePresence mode="wait">
                      {activeMediaIsVideo ? (
                        <motion.video
                          key={activeMedia}
                          src={activeMedia}
                          initial={{ opacity: 0.4, scale: 1.02 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0.4, scale: 0.98 }}
                          transition={{ duration: 0.25 }}
                          className="absolute inset-0 w-full h-full object-contain bg-black"
                          controls
                          controlsList="nodownload"
                          autoPlay
                          disablePictureInPicture
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <motion.img
                          key={activeMedia}
                          src={activeMedia}
                          alt={`${selectedStorm.title} ${activeImageIndex + 1}`}
                          initial={{ opacity: 0.4, scale: 1.02 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0.4, scale: 0.98 }}
                          transition={{ duration: 0.25 }}
                          className="absolute inset-0 h-full w-full object-contain bg-black sm:object-cover"
                        />
                      )}
                    </AnimatePresence>

                    <div className="absolute inset-x-0 top-0 p-4 flex items-center justify-between">
                      <div className="px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full text-xs font-semibold text-white">
                        Media {activeImageIndex + 1} of {selectedStorm.imageCount}
                      </div>
                    </div>

                    {selectedStorm.images.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={showPreviousImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/55 hover:bg-red-500 text-white flex items-center justify-center transition-colors duration-200"
                          aria-label="Show previous storm media item"
                        >
                          <ChevronLeft size={22} />
                        </button>
                        <button
                          type="button"
                          onClick={showNextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/55 hover:bg-red-500 text-white flex items-center justify-center transition-colors duration-200"
                          aria-label="Show next storm media item"
                        >
                          <ChevronRight size={22} />
                        </button>
                      </>
                    )}
                  </div>

                  {selectedStorm.images.length > 1 && (
                    <div className="hidden sm:flex px-4 py-4 border-t border-white/10 bg-black/80 flex-wrap gap-2">
                      {selectedStorm.images.map((image, imageIndex) => (
                        <button
                          key={image}
                          type="button"
                          onClick={() => setActiveImageIndex(imageIndex)}
                          className={`relative w-16 h-16 rounded-lg overflow-hidden border transition-colors duration-200 ${
                            activeImageIndex === imageIndex ? 'border-red-500' : 'border-white/10 hover:border-white/40'
                          }`}
                          aria-label={`Show storm media item ${imageIndex + 1}`}
                        >
                          {isVideoMedia(image) ? (
                            <video
                              src={image}
                              className="w-full h-full object-cover"
                              muted
                              playsInline
                              preload="metadata"
                            />
                          ) : (
                            <img
                              src={image}
                              alt={`${selectedStorm.title} thumbnail ${imageIndex + 1}`}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Details Panel */}
                <div className="lg:w-2/5 p-6 lg:max-h-[80vh] lg:overflow-y-auto lg:p-8">
                  <div className="mb-4 flex flex-wrap items-center gap-3">
                    <h3 className="text-2xl lg:text-3xl font-bold text-white">
                      {selectedStorm.title}
                    </h3>
                    {isRecentStorm(selectedStorm.date) && (
                      <span className="inline-flex items-center rounded-full bg-amber-400/90 px-3 py-1 text-[11px] font-black uppercase tracking-[0.24em] text-black">
                        Recent
                      </span>
                    )}
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3 text-gray-300">
                      <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                        <Calendar size={18} className="text-red-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Date Captured</p>
                        <p className="font-medium">{formatStormDate(selectedStorm.date)}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-gray-300">
                      <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                        <MapPin size={18} className="text-red-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Location</p>
                        <p className="font-medium">{selectedStorm.location}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-gray-300">
                      <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                        <User size={18} className="text-red-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Photographer</p>
                        <p className="font-medium">{selectedStorm.photographer}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-gray-300">
                      <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                        <Camera size={18} className="text-red-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Camera</p>
                        <p className="font-medium">{selectedStorm.camera}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-gray-300">
                      <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                        <Settings2 size={18} className="text-red-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Camera Settings</p>
                        <p className="font-medium font-mono text-sm">{selectedStorm.settings}</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-700 pt-6">
                    <h4 className="text-white font-semibold mb-3">About This Storm</h4>
                    <p className="text-gray-400 leading-relaxed whitespace-pre-line">
                      {selectedStorm.description}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default GallerySection;
